import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicationsAPI } from '../api/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Edit, Pause, Trash2, Play } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const MedsScreen = () => {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);

  const slotNames = {
    before_breakfast: 'Before Breakfast',
    breakfast: 'After Breakfast',
    before_lunch: 'Before Lunch',
    lunch: 'After Lunch',
    before_dinner: 'Before Dinner',
    dinner: 'After Dinner',
    bed: 'Before Sleep',
    prn: 'As Needed',
  };

  useEffect(() => {
    loadMedications();
  }, []);

  const loadMedications = async () => {
    try {
      const data = await medicationsAPI.getAll();
      setMedications(data);
    } catch (error) {
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePause = async (medication) => {
    try {
      await medicationsAPI.update(medication.id, {
        is_active: !medication.is_active,
      });
      toast.success(medication.is_active ? 'Medication paused' : 'Medication resumed');
      loadMedications();
    } catch (error) {
      toast.error('Failed to update medication');
    }
  };

  const handleDelete = async () => {
    if (!selectedMed) return;

    try {
      await medicationsAPI.update(selectedMed.id, { is_active: false });
      toast.success('Medication deleted');
      setDeleteDialogOpen(false);
      setSelectedMed(null);
      loadMedications();
    } catch (error) {
      toast.error('Failed to delete medication');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="meds-screen">
        <div className="text-2xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen" data-testid="meds-screen">
      <div className="px-4 sm:px-6 py-6 space-y-6 max-w-full">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 break-words">My Medications</h1>

        {medications.length === 0 ? (
          <div className="text-center py-16" data-testid="no-medications">
            <div className="text-6xl mb-4">💊</div>
            <p className="text-2xl font-semibold text-gray-600 mb-2">
              No medications yet
            </p>
            <p className="text-lg text-gray-500">
              Tap the + button below to add one
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 max-w-full">
            {medications.map((med) => (
              <div
                key={med.id}
                className={`bg-white rounded-2xl p-4 sm:p-6 shadow-sm border-2 w-full min-w-0 ${
                  med.is_active ? 'border-gray-200' : 'border-gray-300 opacity-60'
                }`}
                data-testid={`medication-tile-${med.id}`}
              >
                <div className="flex flex-col sm:flex-row items-start gap-4 w-full">
                  {med.photo_url && (
                    <img
                      src={med.photo_url}
                      alt={med.display_name}
                      className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl object-cover border-2 border-gray-200"
                    />
                  )}
                  <div className="flex-1 min-w-0 w-full">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 break-words">
                      {med.display_name}
                    </h3>
                    <p className="text-base sm:text-lg text-gray-600 mb-2 break-words overflow-wrap-anywhere">
                      {med.routine_slots && med.routine_slots.length > 1
                        ? med.routine_slots.map((slot) => slotNames[slot]).join(', ')
                        : slotNames[med.routine_slot]}
                      {med.frequency_per_day && med.frequency_per_day > 1 && (
                        <span className="text-gray-500 ml-2 whitespace-nowrap">({med.frequency_per_day}x/day)</span>
                      )}
                    </p>
                    {med.instructions_text && (
                      <p className="text-sm sm:text-base text-gray-500 mb-4 break-words">
                        {med.instructions_text}
                      </p>
                    )}
                    {!med.is_active && (
                      <div className="inline-block px-3 py-1 bg-gray-200 rounded-full text-sm font-semibold text-gray-700 mb-4">
                        Paused
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 w-full">
                      <Button
                        data-testid={`btn-edit-${med.id}`}
                        onClick={() => navigate(`/edit-medication/${med.id}`)}
                        variant="outline"
                        size="sm"
                        className="h-12 px-4 text-base font-semibold flex-1 min-w-[100px]"
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        data-testid={`btn-toggle-${med.id}`}
                        onClick={() => handleTogglePause(med)}
                        variant="outline"
                        size="sm"
                        className="h-12 px-4 text-base font-semibold flex-1 min-w-[100px]"
                      >
                        {med.is_active ? (
                          <>
                            <Pause className="mr-2 h-4 w-4" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="mr-2 h-4 w-4" />
                            Resume
                          </>
                        )}
                      </Button>
                      <Button
                        data-testid={`btn-delete-${med.id}`}
                        onClick={() => {
                          setSelectedMed(med);
                          setDeleteDialogOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="h-12 px-4 text-base font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 flex-1 min-w-[100px]"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold">
              Delete Medication?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-lg">
              This will remove {selectedMed?.display_name} from your list. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete" className="h-14 text-lg font-semibold">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete"
              onClick={handleDelete}
              className="h-14 text-lg font-semibold bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MedsScreen;